import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from "@mui/material";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatFinance } from "../../../../utils/formatters";
import { getMerchantDailySales } from "../../../../api/report/orderStatsReport";

// Predefined colors for merchants
const MERCHANT_COLORS = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7c7c",
  "#8dd1e1",
  "#d084d0",
  "#84d084",
  "#d0d084",
  "#d08484",
  "#84d0d0",
  "#9984d0",
  "#d09984",
  "#84d099",
  "#99d084",
  "#d08499",
];

export const MerchantDailySalesChart = () => {
  const [data, setData] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [selectedMerchants, setSelectedMerchants] = useState([]);
  const [showTop, setShowTop] = useState(5); // Show top 5 merchants by default

  useEffect(() => {
    fetchMerchantData();
  }, []);

  useEffect(() => {
    // Auto-select top merchants when summary data changes
    if (summaryData.length > 0 && selectedMerchants.length === 0) {
      const topMerchants = summaryData
        .slice(0, showTop)
        .map((item) => item.merchant);
      setSelectedMerchants(topMerchants);
    }
  }, [summaryData, showTop]);

  const fetchMerchantData = async () => {
    try {
      setLoading(true);
      const { data } = await getMerchantDailySales();

      setData(data.chart_data);
      setMerchants(data.merchants);
      setSummaryData(data.summary_data);
      setDateRange(data.date_range);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getMerchantColor = (merchant) => {
    const index = merchants.indexOf(merchant);
    return MERCHANT_COLORS[index % MERCHANT_COLORS.length];
  };

  const handleMerchantSelectionChange = (event) => {
    const value = event.target.value;
    setSelectedMerchants(typeof value === "string" ? value.split(",") : value);
  };

  const handleShowTopChange = (event) => {
    const newShowTop = event.target.value;
    setShowTop(newShowTop);

    // Update selected merchants to show top N
    const topMerchants = summaryData
      .slice(0, newShowTop)
      .map((item) => item.merchant);
    setSelectedMerchants(topMerchants);
  };

  // Filter data to only show selected merchants
  const filteredData = data.map((dayData) => {
    const filtered = { date: dayData.date };
    selectedMerchants.forEach((merchant) => {
      filtered[merchant] = dayData[merchant] || 0;
    });
    return filtered;
  });

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Erro ao carregar dados de vendas por estabelecimento: {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Vendas por Estabelecimento (Últimos 30 Dias)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Comparação diária de vendas entre estabelecimentos
        {dateRange && (
          <span>
            {" "}
            ({new Date(dateRange.start_date).toLocaleDateString("pt-BR")} -{" "}
            {new Date(dateRange.end_date).toLocaleDateString("pt-BR")})
          </span>
        )}
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {summaryData.slice(0, 3).map((merchant, index) => (
          <Grid item xs={12} sm={4} key={merchant.merchant}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" component="div" noWrap>
                  {merchant.merchant}
                </Typography>
                <Typography variant="h4" color="primary">
                  {formatFinance(merchant.total_order_value)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {merchant.order_count} pedidos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Controls */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Mostrar Top</InputLabel>
          <Select
            value={showTop}
            label="Mostrar Top"
            onChange={handleShowTopChange}
          >
            <MenuItem value={3}>Top 3</MenuItem>
            <MenuItem value={5}>Top 5</MenuItem>
            <MenuItem value={10}>Top 10</MenuItem>
            <MenuItem value={merchants.length}>Todos</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 300 }}>
          <InputLabel>Estabelecimentos</InputLabel>
          <Select
            multiple
            value={selectedMerchants}
            onChange={handleMerchantSelectionChange}
            input={<OutlinedInput label="Estabelecimentos" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {summaryData.map((merchant) => (
              <MenuItem key={merchant.merchant} value={merchant.merchant}>
                {merchant.merchant} ({formatFinance(merchant.total_order_value)}
                )
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Line Chart */}
      <Box sx={{ height: 400 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={filteredData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={(date) => {
                const d = new Date(date);
                return `${d.getDate()}/${d.getMonth() + 1}`;
              }}
            />
            <YAxis tickFormatter={(value) => formatFinance(value)} />
            <Tooltip
              formatter={(value, name) => [formatFinance(value), name]}
              labelFormatter={(date) => {
                const d = new Date(date);
                return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
              }}
            />
            <Legend />

            {selectedMerchants.map((merchant) => (
              <Line
                key={merchant}
                type="monotone"
                dataKey={merchant}
                stroke={getMerchantColor(merchant)}
                activeDot={{ r: 6 }}
                name={merchant}
                connectNulls={false}
                strokeWidth={2}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>

      {/* Top Merchants List */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Ranking de Estabelecimentos (30 dias)
        </Typography>
        <Grid container spacing={1}>
          {summaryData.map((merchant, index) => (
            <Grid item xs={12} sm={6} md={4} key={merchant.merchant}>
              <Box
                sx={{
                  p: 2,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  backgroundColor: selectedMerchants.includes(merchant.merchant)
                    ? "action.selected"
                    : "transparent",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
                onClick={() => {
                  // Toggle merchant selection when clicking on ranking item
                  if (selectedMerchants.includes(merchant.merchant)) {
                    setSelectedMerchants(
                      selectedMerchants.filter((m) => m !== merchant.merchant)
                    );
                  } else {
                    setSelectedMerchants([
                      ...selectedMerchants,
                      merchant.merchant,
                    ]);
                  }
                }}
              >
                <Typography variant="body1" fontWeight="bold">
                  #{index + 1} {merchant.merchant}
                </Typography>
                <Typography variant="body2" color="primary">
                  {formatFinance(merchant.total_order_value)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {merchant.order_count} pedidos
                </Typography>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: getMerchantColor(merchant.merchant),
                    borderRadius: 1,
                    mt: 1,
                  }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};
